/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { apiClient } from '../../helpers';
import { useNavigate } from 'react-router-dom';
import { Trophy, Star, X, ChevronDown, ChevronUp } from 'lucide-react';

interface Challenge {
  id: number;
  challengerId: number;
  accepterId: number;
  setId: number;
  gameType: 'quiz' | 'matching';
  giftAmount: number;
  status: 'pending' | 'accepted' | 'challenger_played' | 'completed';
  challenger?: {
    id: number;
    name: string;
    avatar: string;
  };
  set?: {
    id: number;
    name: string;
  };
  createdAt: string;
}

const SectionContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const SectionHeader = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #2faff4ff 0%, #79d4f5ff 100%);
  border: none;
  padding: 12px 16px;
  color: white;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #2faff4ff 0%, #79d4f5ff 100%);
  }

  svg {
    width: 20px;
    height: 20px;
    transition: transform 0.3s ease;
  }
`;

const ChallengeContent = styled.div<{ $isOpen: boolean }>`
  max-height: ${(props) => (props.$isOpen ? '500px' : '0')};
  overflow: hidden;
  transition: max-height 0.3s ease;
  padding: ${(props) => (props.$isOpen ? '16px' : '0 16px')};
`;

const ChallengeList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ChallengeCard = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: linear-gradient(135deg, #fafbfd 0%, #f5f7fc 100%);
  border-radius: 8px;
  border-left: 3px solid #ff6b6b;
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #f5f7fc 0%, #f0f3f9 100%);
    transform: translateX(2px);
  }
`;

const AvatarImage = styled.img`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #ff6b6b;
  flex-shrink: 0;
`;

const ChallengeInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChallengerName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-bottom: 3px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ChallengeDetails = styled.div`
  font-size: 12px;
  color: #666;
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const GameTypeBadge = styled.span<{ $type: 'quiz' | 'matching' }>`
  display: inline-flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  background: ${(props) => (props.$type === 'quiz' ? '#e3f2fd' : '#f3e5f5')};
  color: ${(props) => (props.$type === 'quiz' ? '#1976d2' : '#7b1fa2')};
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
`;

const GiftBadge = styled.span`
  display: flex;
  align-items: center;
  gap: 3px;
  padding: 2px 6px;
  background: #fff3cd;
  color: #856404;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  
  svg {
    color: #ffc107;
    fill: #ffc107;
  }
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 6px;
  flex-shrink: 0;
`;

const Button = styled.button<{ $variant?: 'primary' | 'danger' }>`
  padding: 6px 10px;
  border: none;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;

  ${(props) => {
    if (props.$variant === 'danger') {
      return `
        background: #fee;
        color: #c33;
        &:hover {
          background: #fdd;
          color: #a22;
        }
      `;
    }
    return `
      background: linear-gradient(90deg, #4CAF50 0%, #45a049 100%);
      color: white;
      &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(76, 175, 80, 0.3);
      }
    `;
  }}
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 20px;
  color: #999;
  font-size: 13px;
`;

// Modal Overlay
const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  align-items: center;
  justify-content: center;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.2);
  text-align: center;
`;

const ModalTitle = styled.h3`
  margin: 0 0 12px 0;
  font-size: 18px;
  color: #333;
`;

const ModalMessage = styled.p`
  margin: 0 0 20px 0;
  font-size: 14px;
  color: #666;
  line-height: 1.5;
`;

const ModalButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
`;

const ModalButton = styled.button<{ $variant?: 'cancel' | 'danger' }>`
  padding: 10px 20px;
  border: none;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  ${props => {
    if (props.$variant === 'danger') {
      return `
        background: #dc3545;
        color: white;
        &:hover {
          background: #c82333;
        }
      `;
    }
    return `
      background: #e9ecef;
      color: #495057;
      &:hover {
        background: #dee2e6;
      }
    `;
  }}
`;

interface PendingChallengesSectionProps {
  studentId?: number;
}

const PendingChallengesSection: React.FC<PendingChallengesSectionProps> = ({ studentId }) => {
  const [challenges, setChallenges] = useState<Challenge[]>([]);
  const [loading, setLoading] = useState(true);
  const [isOpen, setIsOpen] = useState(false);
  const [rejectModalOpen, setRejectModalOpen] = useState(false);
  const [selectedChallengeId, setSelectedChallengeId] = useState<number | null>(null);
  const [selectedChallengerName, setSelectedChallengerName] = useState<string>('');
  const navigate = useNavigate();

  useEffect(() => {
    if (!studentId) return;

    const fetchChallenges = async () => {
      try {
        setLoading(true);
        const response = await apiClient.get(`/api/challenges/pending/${studentId}`);
        setChallenges(response.data || []);
      } catch (error) {
        console.error('Failed to fetch pending challenges:', error);
        setChallenges([]);
      } finally {
        setLoading(false);
      }
    };

    fetchChallenges();
  }, [studentId]);

  const handleAcceptChallenge = async (challengeId: number, setId: number, gameType: 'quiz' | 'matching') => {
    try {
      // Remove from list immediately for better UX
      setChallenges(challenges.filter((c) => c.id !== challengeId));

      // Navigate to the quiz or matching game with challenge state
      // The game will automatically submit the accepter's score when completed
      const gameRoute = gameType === 'quiz' ? 'quiz' : 'matching';
      navigate(`/student/flashcards/sets/${setId}/${gameRoute}`, {
        state: { gameType, challengeId, isChallenge: true, isAccepter: true }
      });
    } catch (error) {
      console.error('Failed to accept challenge:', error);
      alert('Không thể chấp nhận lời thách đấu');
    }
  };

  const handleRejectChallenge = async (challengeId: number, challengerName: string) => {
    setSelectedChallengeId(challengeId);
    setSelectedChallengerName(challengerName);
    setRejectModalOpen(true);
  };

  const confirmRejectChallenge = async () => {
    if (!selectedChallengeId) return;

    try {
      await apiClient.delete(`/api/challenges/${selectedChallengeId}`);
      setChallenges(challenges.filter((c) => c.id !== selectedChallengeId));
      setRejectModalOpen(false);
      setSelectedChallengeId(null);
      setSelectedChallengerName('');
    } catch (error) {
      console.error('Failed to reject challenge:', error);
      setRejectModalOpen(false);
      // Show error in a more user-friendly way
    }
  };

  const cancelRejectChallenge = () => {
    setRejectModalOpen(false);
    setSelectedChallengeId(null);
    setSelectedChallengerName('');
  };

  if (loading) {
    return (
      <SectionContainer>
        <SectionHeader onClick={() => setIsOpen(!isOpen)}>
          <span>🎮 Lời Thách Đấu</span>
          {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
        </SectionHeader>
      </SectionContainer>
    );
  }

  return (
    <SectionContainer>
      <SectionHeader onClick={() => setIsOpen(!isOpen)}>
        <span>🎮 Lời Thách Đấu ({challenges.length})</span>
        {isOpen ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
      </SectionHeader>

      <ChallengeContent $isOpen={isOpen}>
        {challenges.length === 0 ? (
          <EmptyState>Chưa có lời thách đấu nào</EmptyState>
        ) : (
          <ChallengeList>
            {challenges.map((challenge) => (
              <ChallengeCard key={challenge.id}>
                {challenge.challenger?.avatar && (
                  <AvatarImage src={challenge.challenger.avatar} alt={challenge.challenger.name} />
                )}
                <ChallengeInfo>
                  <ChallengerName>{challenge.challenger?.name || 'Unknown'}</ChallengerName>
                  <ChallengeDetails>
                    <GameTypeBadge $type={challenge.gameType}>
                      {challenge.gameType === 'quiz' ? '📝 Trắc Nghiệm' : '🃏 Ghép Thẻ'}
                    </GameTypeBadge>
                    <GiftBadge>
                      <Star size={12} />
                      {challenge.giftAmount}
                    </GiftBadge>
                  </ChallengeDetails>
                </ChallengeInfo>
                <ActionButtons>
                  <Button
                    onClick={() =>
                      handleAcceptChallenge(
                        challenge.id,
                        challenge.setId,
                        challenge.gameType
                      )
                    }
                  >
                    ✓ Chơi
                  </Button>
                  <Button $variant="danger" onClick={() => handleRejectChallenge(challenge.id, challenge.challenger?.name || 'Unknown')}>
                    ✕ Từ chối
                  </Button>
                </ActionButtons>
              </ChallengeCard>
            ))}
          </ChallengeList>
        )}
      </ChallengeContent>

      {/* Modal xác nhận từ chối */}
      <ModalOverlay $isOpen={rejectModalOpen} onClick={cancelRejectChallenge}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <ModalTitle>❌ Từ chối lời thách đấu?</ModalTitle>
          <ModalMessage>
            Bạn có chắc muốn từ chối lời thách đấu từ <strong>{selectedChallengerName}</strong>?
          </ModalMessage>
          <ModalButtons>
            <ModalButton onClick={cancelRejectChallenge}>
              Hủy
            </ModalButton>
            <ModalButton $variant="danger" onClick={confirmRejectChallenge}>
              Xác nhận từ chối
            </ModalButton>
          </ModalButtons>
        </ModalContent>
      </ModalOverlay>
    </SectionContainer>
  );
};

export default PendingChallengesSection;
